import { PlusIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import React from "react";

import { Button } from "../ui/button";
import { GifType, Wrangler } from "../ui/Wrangler";

import CreateContextMenu from "./CreateContextMenu";
import RegistryLoadingState from "./RegistryLoadingState";
import { RegistryItem, SortState } from "./types";
import { useUnifiedRegistry } from "./unified-registry-context";
import VirtualizedCardGrid from "./VirtualizedCardGrid";
import VirtualizedListView from "./VirtualizedListView";
import { ViewMode } from "./ViewToggle";

import RequestVolumeTail from "@/components/RequestVolumeTail";

export interface RegistryCoreContentProps {
  shouldShowLoading: boolean;
  hasNoResults: boolean;
  showBanner: boolean;
  viewMode: ViewMode;
  folders: RegistryItem[];
  items: RegistryItem[];
  allItems: RegistryItem[];
  textResultCount: number;
  isLoading: boolean;
  currentFolderId: number | null;
  workspaceId: number | null;
  sortState?: SortState;
  isCreatingItem: boolean;
  onCreatePrompt: () => void;
  onItemClick?: (item: RegistryItem) => void;
}

const RegistryCoreContent = ({
  shouldShowLoading,
  hasNoResults,
  showBanner,
  viewMode,
  folders,
  items,
  allItems,
  textResultCount,
  isLoading,
  currentFolderId,
  workspaceId,
  sortState,
  isCreatingItem,
  onCreatePrompt,
  onItemClick,
}: RegistryCoreContentProps) => {
  const registry = useUnifiedRegistry();

  if (shouldShowLoading) {
    return (
      <div className="h-full bg-white">
        {registry.hasActiveFilters ? (
          <Wrangler variant={GifType.Searching} size="lg" className="h-full px-8" />
        ) : (
          <RegistryLoadingState showBanner={showBanner} />
        )}
      </div>
    );
  }

  if (hasNoResults) {
    return (
      <div className="flex h-full flex-col">
        {showBanner && <RequestVolumeTail />}
        <CreateContextMenu
          className="h-full"
          currentFolderId={currentFolderId}
          disabled={registry.hasActiveFilters}
        >
          <div className="mt-12 flex flex-1 flex-col items-center justify-center px-8 text-center">
            <Wrangler variant={GifType.Searching} size="lg" hideText />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              No items {!registry.hasActiveFilters ? "yet" : "found"}
            </h3>
            <p className="mb-6 text-gray-500">
              {registry.hasActiveFilters
                ? "Try adjusting your search or filter criteria to find what you're looking for."
                : "Create your first prompt to get started."}
            </p>
            {!registry.hasActiveFilters && (
              <Button
                onClick={onCreatePrompt}
                isLoading={isCreatingItem}
                variant="default"
                size="lg"
              >
                <div className="flex h-5 w-5 items-center justify-center rounded bg-white/20">
                  <PlusIcon className="h-3.5 w-3.5 text-white" />
                </div>
                <span className="ml-2">Create New Prompt</span>
              </Button>
            )}
          </div>
        </CreateContextMenu>
      </div>
    );
  }

  if (viewMode === "grid") {
    return (
      <VirtualizedCardGrid
        folders={folders}
        items={items}
        allItems={allItems}
        textResultCount={textResultCount}
        showBanner={showBanner}
        currentFolderId={currentFolderId}
        workspaceId={workspaceId}
        onItemClick={onItemClick}
      />
    );
  }

  return (
    <VirtualizedListView
      items={allItems}
      textResultCount={textResultCount}
      isLoading={isLoading}
      showBanner={showBanner}
      currentFolderId={currentFolderId}
      sortField={sortState?.field}
      sortDirection={sortState?.direction}
      onSort={field => registry.handleSort(field)}
      onItemClick={onItemClick}
    />
  );
};

export default observer(RegistryCoreContent);
